import { auth, profile } from '~api';
import User from '~models/User';
import { getField, updateField } from 'vuex-map-fields';
import $api, { Handler, PushNotificationsNative } from '~services';
import router from '~router';
import i18n from '../i18n';

const LOGIN_SUCCESS = 'LOGIN_SUCCESS';
const LOGOUT = 'LOGOUT';
const LOADING = 'LOADING';
const SET_USER = 'SET_USER';

const localUser =
    window.localStorage.getItem('user') !== 'undefined'
        ? JSON.parse(localStorage.getItem('user'))
        : {};

const state = {
    token: localStorage.getItem('token') || false,
    user: {
        ...new User(),
        ...localUser
    },
    isLoading: false
};

const actions = {
    /**
     * Login user and set data to local storage
     * @param {function} commit
     * @param params {object} params
     * @returns {Promise<any>}
     */
    async login({ commit }, params) {
        let errorMessage = i18n.t('auth.login.errorMessage');
        const resp = await auth.login(params);

        let RedirectPage = 'dashboard.themes';

        if (resp.ok) {
            const token = resp.data[Object.keys(resp.data)[0]];

            $api.setHeader('Authorization', `Bearer ${token}`);

            window.localStorage.setItem('token', token);

            const profileResp = await profile.getProfile();

            if (profileResp.ok) {
                const { data } = profileResp;

                if (data.role && data.role === 'admin') {
                    RedirectPage = 'dashboard.themes';
                }

                window.localStorage.setItem('user', JSON.stringify(data));

                commit(LOGIN_SUCCESS, { token, data });
            }
        } else {
            window.localStorage.removeItem('user');
            window.localStorage.removeItem('token');

            if (
                resp.data &&
                resp.data.message &&
                typeof resp.data.message === 'string' &&
                resp.data.code &&
                resp.data.code === 'bad_request'
            ) {
                errorMessage = resp.data.message;
            }
        }

        Handler.handleResponse(
            resp,
            undefined,
            errorMessage,
            RedirectPage,
            params.redirect
        );
    },

    async updateUserData(_context, userData) {
        const resp = await profile.updateInstructorInformation({
            position: userData.position,
            information: userData.information
        });

        if (resp.ok) {
            Handler.handleResponse(
                resp,
                i18n.t('profile.successMessagePersonalInformation')
            );
        }

        const { data } = await profile.getProfile();

        window.localStorage.setItem('user', JSON.stringify(data));
    },

    /**
     * Change password and set data to local storage
     * @param {function} commit
     * @param params {object} params
     * @returns {Promise<any>}
     */
    async confirmPassword({ commit }, params) {
        commit(LOADING);

        const successMessage = i18n.t('auth.firstLogin.successMessage');
        const errorMessage = i18n.t('auth.firstLogin.errorMessage');

        const resp = await auth.confirmPassword(params);

        if (resp.ok) {
            const token = resp.data.token;

            $api.setHeader('Authorization', `Bearer ${token}`);

            window.localStorage.setItem('token', token);

            const profileResp = await profile.getProfile();

            if (profileResp.ok) {
                const { data } = profileResp;

                window.localStorage.setItem('user', JSON.stringify(data));

                commit(LOGIN_SUCCESS, { token, data });
            }
        } else {
            Handler.handleResponse(resp);
        }

        Handler.handleResponse(
            resp,
            successMessage,
            errorMessage,
            'dashboard.themes'
        );

        commit(LOADING);
    },

    async getProfile({ commit }) {
        const profileResp = await profile.getProfile();

        if (profileResp.ok) {
            const { data } = profileResp;

            window.localStorage.setItem('user', JSON.stringify(data));

            commit(SET_USER, data);
        }
    },

    /**
     * Confirm email
     * @param {function} commit
     * @param params {object} params
     * @returns {Promise<any>}
     */
    async confirmEmail({ commit, rootGetters }, params) {
        const successMessage = i18n.t('auth.confirmEmail.successMessage');
        const errorMessage = i18n.t('auth.confirmEmail.errorMessage');
        const packageType = rootGetters['payments/packageType'];
        let redirectLink = 'dashboard.paymentInformation';

        commit(LOADING);

        if (packageType === 'free' || packageType === 'corporate') {
            redirectLink = 'dashboard.themes';
        }

        const resp = await auth.confirmEmail(params);

        if (resp.ok) {
            const token = resp.data.token;

            $api.setHeader('Authorization', `Bearer ${token}`);

            window.localStorage.setItem('token', token);

            const profileResp = await profile.getProfile();

            if (profileResp.ok) {
                const { data } = profileResp;

                if (data.status !== 'trial') {
                    redirectLink = 'dashboard.themes';
                }

                window.localStorage.setItem('user', JSON.stringify(data));

                commit(LOGIN_SUCCESS, { token, data });
            }
        }

        Handler.handleResponse(
            resp,
            successMessage,
            errorMessage,
            redirectLink
        );

        commit(LOADING);
    },

    /**
     * Resend email
     * @param {function} commit
     * @param params {object} params
     * @returns {Promise<any>}
     */
    async resendEmail({ commit }, email) {
        const successMessage = 'Confirmation email resent';
        const errorMessage = i18n.t('auth.confirmEmail.errorMessage');

        commit(LOADING);

        const resp = await auth.resendEmail({ email });

        Handler.handleResponse(resp, successMessage, errorMessage);

        commit(LOADING);
    },
    /**
     * Password reset
     * @param {function} commit
     * @param params {object} params
     * @returns {Promise<any>}
     */
    async passwordReset({ commit }, params) {
        commit(LOADING);

        const successMessage = i18n.t('auth.forgotPassword.successMessage');
        const resp = await auth.passwordSendEmail(params);

        Handler.handleResponse(resp, successMessage, undefined, 'auth.login');

        commit(LOADING);
    },

    /**
     * Logout user and clear local storage
     * @param {function} commit
     */
    async logout({ commit }) {
        // Tell the backend to forget this device's push endpoint before we
        // drop the auth bearer — the DELETE requires auth.
        try {
            await PushNotificationsNative.removePushnotifications();
        } catch (err) {
            // Non-fatal: logout must still proceed even if the backend is
            // unreachable, otherwise the user gets stuck.
            console.warn('[auth] push unregister on logout failed', err);
        }

        // Web push (OneSignal): clear the external user id + email so the
        // browser/PWA session stops matching pushes to the previous account.
        // Wrapped defensively because the OneSignal SDK may be absent (native
        // build, blocked CDN, no-consent locale).
        try {
            if (
                window.OneSignal &&
                typeof window.OneSignal.push === 'function'
            ) {
                window.OneSignal.push(() => {
                    try {
                        window.OneSignal.removeExternalUserId &&
                            window.OneSignal.removeExternalUserId();
                    } catch (_) {
                        // OneSignal may not be fully initialized — ignore
                    }
                    try {
                        window.OneSignal.logoutEmail &&
                            window.OneSignal.logoutEmail();
                    } catch (_) {
                        // OneSignal may not be fully initialized — ignore
                    }
                });
            }
        } catch (err) {
            console.warn('[auth] OneSignal logout cleanup failed', err);
        }

        commit(LOGOUT);
        window.localStorage.removeItem('token');
        window.localStorage.removeItem('user');
        window.localStorage.removeItem('packageType');
    },

    /**
     * Register user and redirect to confirmation registration page
     * @param {function} commit
     * @param params {object} params
     * @returns {Promise<any>}
     */
    async registration({ commit }, params) {
        commit(LOADING);

        const { model, isPublic } = params;

        let data = {};

        if (isPublic) {
            data = model;
        } else {
            const {
                fullName,
                email,
                phone,
                password,
                passwordConfirmation
            } = model;
            data = { fullName, email, phone, password, passwordConfirmation };
        }

        const resp = await auth.signUp(data);

        if (resp.ok) {
            router.push({
                name: 'auth.confirmRegistration',
                params: {
                    email: model.email
                }
            });
        } else {
            Handler.handleResponse(resp);
        }

        commit(LOADING);
    },

    /**
     * Register user and login Hospital
     * @param {function} commit
     * @param params {object} params
     * @returns {Promise<any>}
     */
    async registrationByBusiness({ commit }, params) {
        commit(LOADING);

        const resp = await auth.signUp(params);

        if (resp.ok) {
            const token = resp?.data?.token;

            $api.setHeader('Authorization', `Bearer ${token}`);

            localStorage.setItem('token', token);

            const profileResp = await profile.getProfile();

            if (profileResp.ok) {
                const { data } = profileResp;

                localStorage.setItem('user', JSON.stringify(data));

                commit(LOGIN_SUCCESS, { token, data });

                await router.push({ name: 'dashboard.themes' });
            } else {
                Handler.handleResponse(resp, '', null, null);
            }
        } else {
            Handler.handleResponse(resp, '', null, null);
        }

        commit(LOADING);
    }
};

const mutations = {
    // Add the `updateField` mutation to the
    // `mutations` of your Vuex store instance.
    updateField,
    [LOGIN_SUCCESS](state, data) {
        state.token = data.token;
        state.user = {
            ...new User(),
            ...JSON.parse(localStorage.getItem('user'))
        };
    },
    [SET_USER](state, data) {
        state.user = data;
    },
    [LOGOUT](state) {
        state.token = false;
        state.user = {
            ...new User()
        };
    },
    [LOADING](state) {
        state.isLoading = !state.isLoading;
    }
};

const getters = {
    // Add the `getField` getter to the
    // `getters` of your Vuex store instance.
    getField,
    id: state => state.id,
    showShareButton: state =>
        state.user.businessId === '0fffd7ca543d7a55a0050541e0573108',
    isAwit: state =>
        state.user.business_id === 'f8638a6c3309fb7513f870673fe7863f',
    isAnthem: state =>
        state.user.business_id === '2f14dbe67ab4e9fbec35ba108b56b2b9',
    isGoldman: state =>
        state.user.business_id === '0fffd7ca543d7a55a0050541e0573108',
    user: state => state.user,
    role: state => (state.user ? state.user.role : ''),
    status: state => state.user.status,
    isLoading: state => state.isLoading,
    avatar: state => state.user.avatar,
    subscriptionStatus: state => state.user.subscriptionStatus,
    notAllowToSubscribe: state =>
        state.user.subscriptionStatus === 'active' ||
        (state.user.subscription?.canceledAt == null &&
            state.user.subscription?.deletedAt === null &&
            state.user.subscriptionStatus === 'trial'),
    isStripeSubscription: state =>
        state.user.subscription && state.user.subscription.type === 'stripe',
    isAppleSubscription: state =>
        state.user.subscription && state.user.subscription.type === 'app_store',
    stripeId: state => state.user.stripe_customer_id,
    showPaymentPopup: state =>
        (state.user.status === 'expired' ||
            state.user.status === 'discontinued') &&
        state.user.role === 'member',
    showPaymentProfile: state => {
        if (state.user.subscription && !state.user.subscription.canceledAt) {
            return true;
        }

        return (
            (state.user.status === 'discontinued' ||
                state.user.status === 'monthly' ||
                state.user.status === 'annual') &&
            state.user.role === 'member'
        );
    },

    isChallenge: state => {
        if (state.user.isChallenge) return state.user.isChallenge;
        if (state.user.business) return state.user.business.isChallenge;
        return false;
    },
    isSubscribed: state => state.user.isSubscribed,

    /**
     * Check if user role equals champion
     * @param state
     * @returns {boolean}
     */
    isChampion: state => state.user.role === 'champion',

    /**
     * Check if user role equals member
     * @param state
     * @returns {boolean}
     */
    isMember: state => state.user.role === 'member',

    /**
     * Check if user role equals admin
     * @param state
     * @returns {boolean}
     */
    isAdmin: state => state.user.role === 'admin',

    /**
     * Check if user role equals instructor
     * @param state
     * @returns {boolean}
     */
    isInstructor: state => state.user.role === 'instructor',

    /**
     * Show department if max users more then 15
     * @param state
     * @returns {boolean}
     */
    showDepartment: state => {
        return state.user && state.user.business && state.user.business.maxUsers
            ? state.user.business.maxUsers >= 15
            : false;
    }
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
};
